import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import {
  useBlockProps,
  RichText,
  InspectorControls,
  ColorPalette,
  useSettings,
} from '@wordpress/block-editor';
import { Button, BaseControl } from '@wordpress/components';

import PanelBody from '../../components/PanelBody';
import IconsModal from '../../components/IconsModal';
import {
  fromColorPreset,
  getIconClasses,
  resolvePresetValue,
  toColorPreset,
} from '../../utils/helpers';

/**
 * One line of an Icon List.
 *
 * The icon is per item (a checklist mixes ticks and crosses) while its size,
 * colour and spacing come from the parent through context — the numbers an
 * author sets once should not need setting on every line.
 *
 * Links are inline RichText formats rather than a block-level link control:
 * a line often wants only one word linked, and core's own list items work the
 * same way. An earlier revision wired up a LinkControl popover in the block
 * toolbar and hung the editor's renderer; the inline format needs no extra
 * machinery to do more.
 */
export default function Edit({ attributes, setAttributes, context }) {
  const { iconName, iconType, text, iconColor } = attributes;

  const [isIconsModalOpen, setIsIconsModalOpen] = useState(false);
  const [palette] = useSettings('color.palette');

  /*
   * Text colour is core's own support, so it lands on the wrapper and the
   * label inherits it. The icon needs its own value: it is painted from
   * `--brandy-icon-list-icon-color`, which the list sets for every item —
   * writing it here overrides the list for this line only, and leaving it
   * empty lets the list's value through untouched.
   */
  const blockProps = useBlockProps({
    className: 'brandy-icon-list__item',
    style: {
      '--brandy-icon-list-icon-size': `${context['brandy/iconListIconSize'] || 20}px`,
      '--brandy-icon-list-icon-gap': `${context['brandy/iconListIconGap'] ?? 10}px`,
      // Expanded exactly as save.jsx does, so the canvas matches the frontend.
      ...(iconColor
        ? { '--brandy-icon-list-icon-color': resolvePresetValue(iconColor) }
        : {}),
    },
  });

  const iconClasses = getIconClasses(iconName, iconType);

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Icon', 'brandy-blocks')} initialOpen>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {iconName && <i className={iconClasses} style={{ fontSize: '24px' }} />}
            <Button variant="secondary" onClick={() => setIsIconsModalOpen(true)}>
              {iconName
                ? __('Change icon', 'brandy-blocks')
                : __('Choose icon', 'brandy-blocks')}
            </Button>
            {iconName && (
              <Button
                variant="tertiary"
                isDestructive
                onClick={() => setAttributes({ iconName: '' })}
              >
                {__('Remove', 'brandy-blocks')}
              </Button>
            )}
          </div>

          {iconName && (
            <BaseControl
              __nextHasNoMarginBottom
              label={__('Icon colour', 'brandy-blocks')}
              help={__(
                'This line only. Clear it to follow the list’s icon colour.',
                'brandy-blocks'
              )}
            >
              <ColorPalette
                colors={palette || []}
                value={fromColorPreset(iconColor, palette)}
                onChange={(value) =>
                  setAttributes({ iconColor: toColorPreset(value, palette) || '' })
                }
                clearable
                __experimentalIsRenderedInSidebar
              />
            </BaseControl>
          )}
        </PanelBody>
      </InspectorControls>

      <li {...blockProps}>
        {iconClasses && <i className={iconClasses} aria-hidden="true" />}
        <RichText
          tagName="span"
          className="brandy-icon-list__text"
          value={text}
          onChange={(value) => setAttributes({ text: value })}
          placeholder={__('Write a line…', 'brandy-blocks')}
        />
      </li>

      <IconsModal
        isIconsModalOpen={isIconsModalOpen}
        setIsIconsModalOpen={setIsIconsModalOpen}
        icon={{ name: iconName, type: iconType }}
        setAttributes={(newAttrs) =>
          setAttributes({ iconName: newAttrs.name, iconType: newAttrs.type })
        }
      />
    </>
  );
}
